
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import PlatformLogsFilters from '@/components/appComponents/PlatformLogsFilters.vue';
import PlatformLogsTable from '@/components/appComponents/PlatformLogsTable.vue';
import PlatformLogsPagination from '@/components/appComponents/PlatformLogsPagination.vue';
import PlatformLogDetails from '@/components/appComponents/PlatformLogDetails.vue';
import { PlatformLog, PlatformLogsFilters as IFilters } from '@/interfaces/panel/platform-logs.model';

export default defineComponent({
  name: 'PlatformLogs',
  components: {
    PlatformLogsFilters,
    PlatformLogsTable,
    PlatformLogsPagination,
    PlatformLogDetails,
  },
  setup() {
    const store = useStore();

    const selectedLog = ref<PlatformLog | null>(null);
    const showDetailsDialog = ref(false);

    // Computed properties from store
    const platformLogs = computed(() => store.getters['platformLogs/platformLogs']);
    const pagination = computed(() => store.getters['platformLogs/pagination']);
    const stats = computed(() => store.getters['platformLogs/stats']);
    const filters = computed(() => store.getters['platformLogs/filters']);
    const loading = computed(() => store.getters['platformLogs/loading']);
    const totalItems = computed(() => store.getters['platformLogs/totalItems']);
    const totalPages = computed(() => store.getters['platformLogs/totalPages']);

    // Methods
    const fetchLogs = async () => {
      try {
        await store.dispatch('platformLogs/fetchPlatformLogs', filters.value);
      } catch (error) {
        console.error('Error fetching logs:', error);
        // You can add a notification/toast here
      }
    };

    const handleApplyFilters = async (newFilters: IFilters) => {
      try {
        await store.dispatch('platformLogs/updateFilters', {
          ...newFilters,
          page: 1, // Reset to first page when applying filters
        });
      } catch (error) {
        console.error('Error applying filters:', error);
      }
    };

    const handleResetFilters = async () => {
      try {
        await store.dispatch('platformLogs/resetFilters');
      } catch (error) {
        console.error('Error resetting filters:', error);
      }
    };

    const handlePageChange = async (page: number) => {
      try {
        await store.dispatch('platformLogs/updateFilters', {
          ...filters.value,
          page,
        });
      } catch (error) {
        console.error('Error changing page:', error);
      }
    };

    const handlePageSizeChange = async (pageSize: number) => {
      try {
        await store.dispatch('platformLogs/updateFilters', {
          ...filters.value,
          page: 1, // Reset to first page when changing page size
          pageSize,
        });
      } catch (error) {
        console.error('Error changing page size:', error);
      }
    };

    const handleViewDetails = (log: PlatformLog) => {
      selectedLog.value = log;
      showDetailsDialog.value = true;
    };

    const handleCloseDetails = () => {
      showDetailsDialog.value = false;
      selectedLog.value = null;
    };

    const formatNumber = (num: number) => {
      return new Intl.NumberFormat('fa-IR').format(num);
    };

    // Lifecycle
    onMounted(() => {
      fetchLogs();
    });

    return {
      platformLogs,
      pagination,
      stats,
      filters,
      loading,
      totalItems,
      totalPages,
      selectedLog,
      showDetailsDialog,
      handleApplyFilters,
      handleResetFilters,
      handlePageChange,
      handlePageSizeChange,
      handleViewDetails,
      handleCloseDetails,
      formatNumber,
    };
  },
});
